import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import {
  type Task,
  task,
  type TaskForAsyncTaskFunction,
  type TaskInstance,
} from 'ember-concurrency';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import type {
  BenchmarkInsightCompetitorsDesiredCompset,
  BeniBreakdown,
  BeniBreakdownPeriodSegmentDifference,
  BeniDataApiResponse,
  BeniPaceApiResponse,
  BeniUpsell,
} from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import {
  ALL_SEGMENTS_OPTION,
  BENI_SEGMENTATIONS_API_IDS,
  BENI_SEGMENTATIONS_API_MODEL_NAMES,
  BENI_SEGMENTATIONS_API_URLS,
  BREAKDOWNS,
  DEFAULT_DAYS_SHOWN_IN_BENI,
  type Kpi,
  KPIS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { DROPDOWN_COMPARISON_SETS_OPTION_NONE } from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';
import BENI_LOCAL_STORAGE_KEYS from 'lumen/utils/constants/benchmark-insight/local-storage/beni-local-storage-constants';
import { PMS_MAPPING_TABS } from 'lumen/utils/constants/pms-mapping-constants';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';
import type {
  Breakdown,
  HierarchyItem,
} from 'lumen/utils/datamodels/mapping-category-benchmark';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type BenchmarkInsightParams = {
  breakdownQuery: string;
  channel: string;
  extractDate: string;
  fromDate: string;
  los: string;
  pickup: string;
  primaryDataSet: string;
  referencePeriod: string;
  secondaryDataSet: string;
  segment: string;
  selectedAxisOption: string;
  selectedChannelOccupancyView: string;
  selectedKpiChannel: string;
  selectedKpiHeroChart: string;
  selectedKpiLosChart: string;
  selectedLosOccupancyView: string;
  selectedLosSortOption: string;
  stayDateSectionView: string;
  toDate: string;
  viewAsStaffUser: string;
};

type ModelType = {
  dataApiResponse?: Task<string, [params: BenchmarkInsightParams]>;
  desiredCompset?: TaskForAsyncTaskFunction<
    unknown,
    () => Promise<BenchmarkInsightCompetitorsDesiredCompset | null>
  >;
  hierarchy?: TaskInstance<MappingCategoryBenchmark>;
  hierarchyChannel?: TaskInstance<MappingCategoryBenchmark>;
  modelTask?: Task<void, [params: BenchmarkInsightParams]>;
  paceApiResponse?: Task<string, [params: BenchmarkInsightParams]>;
  upsell?: TaskForAsyncTaskFunction<
    { restartable: boolean },
    () => Promise<BeniUpsell | null>
  >;
};

type HierarchyResponse = {
  hierarchy: HierarchyItem[];
};

export default class HotelBenchmarkInsightRoute extends Route {
  queryParams = {
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    segment: {
      refreshModel: true,
    },
    los: {
      refreshModel: true,
    },
    channel: {
      refreshModel: true,
    },
    selectedKpiHeroChart: {
      refreshModel: false,
    },
    selectedKpiLosChart: {
      refreshModel: false,
    },
    selectedLosSortOption: {
      refreshModel: false,
    },
    selectedLosChartOccupancyView: {
      refreshModel: false,
    },
    selectedAxisOption: {
      refreshModel: false,
    },
    dateAggregation: {
      refreshModel: false,
    },
    referencePeriod: {
      refreshModel: true,
    },
    extractDate: {
      refreshModel: true,
    },
    stayDateSectionView: {
      refreshModel: false,
    },
    pickup: {
      refreshModel: true,
    },
    primaryDataSet: {
      refreshModel: true,
    },
    secondaryDataSet: {
      refreshModel: true,
    },
    viewAsStaffUser: {
      refreshModel: true,
    },
    breakdownQuery: {
      refreshModel: true,
    },
    currency: {
      refreshModel: true,
    },
    expandedBreakdowns: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];
  @service('benchmark-insight/trial')
  declare benchmarkInsightTrial: Services['benchmark-insight/trial'];
  @service('benchmark-insight/beni-hotels')
  declare beniHotelsService: Services['benchmark-insight/beni-hotels'];

  @service('benchmark-insight/beni-service')
  declare beniService: Services['benchmark-insight/beni-service'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];
  @service declare userEngagement: Services['user-engagement'];

  hasSetQueryParametersFromLocalStorage = false;

  dataApiResponseTask = task(async (params) => {
    try {
      const path = BENI_SEGMENTATIONS_API_URLS[BENI_SEGMENTATIONS_API_IDS.DATA];
      if (!path) {
        return null;
      }

      const requestUrl = formatUrl(
        path,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.getQueryParams(params),
        this.ajax.host,
      );

      const apiResponse = await this.ajax.request(requestUrl);

      const apiId = BENI_SEGMENTATIONS_API_IDS.DATA;
      const segmentationModelName: string =
        BENI_SEGMENTATIONS_API_MODEL_NAMES[apiId] ?? '';

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload({
        [segmentationModelName]: apiResponse,
      });

      const apiModelName = BENI_SEGMENTATIONS_API_MODEL_NAMES[apiId];
      if (apiModelName) {
        const peekedBeniApiResponse =
          this.store.peekRecord<BeniDataApiResponse>(
            // @ts-expect-error: Incorrect type
            apiModelName,
            BENI_SEGMENTATIONS_API_IDS.DATA,
          );

        this.addDifferenceToSegmentations(
          // @ts-expect-error: 'peekedBeniApiResponse' is possibly 'null'.
          peekedBeniApiResponse.filteredDaySegmentations,
        );
        this.addDifferenceToSegmentations(
          // @ts-expect-error: 'peekedBeniApiResponse' is possibly 'null'.
          peekedBeniApiResponse.filteredWeekSegmentations,
        );
        this.addDifferenceToSegmentations(
          // @ts-expect-error: 'peekedBeniApiResponse' is possibly 'null'.
          peekedBeniApiResponse.filteredSegmentSegmentations,
        );
        this.addDifferenceToSegmentations(
          // @ts-expect-error: 'peekedBeniApiResponse' is possibly 'null'.
          peekedBeniApiResponse.filteredLosSegmentations,
        );
        this.addDifferenceToSegmentations(
          // @ts-expect-error: 'peekedBeniApiResponse' is possibly 'null'.
          peekedBeniApiResponse.filteredChannelSegmentations,
        );

        return peekedBeniApiResponse;
      }
    } catch (error) {
      this.errorTracking.captureException(error);
    }

    return null;
  });

  fetchHierarchyTask = task({}, async (breakdown: Breakdown) => {
    const { hotel } = this.hotelAndMarketService;

    const queryParams: FormatUrlParams = {
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
      subscription_id: hotel.id,
      breakdown,
    };

    const hierarchyUrl = formatUrl(
      '/api/v3/benchmarkinsight/mapping/hierarchy',
      queryParams,
      this.ajax.host,
    );

    try {
      const response = await this.ajax.request(hierarchyUrl);

      if (!this.isHierarchyResponse(response)) {
        throw new Error(
          `Invalid hierarchy response: ${JSON.stringify(response)}`,
        );
      }

      return this.benchmarkInsightBeniService.createRootCategory(
        response.hierarchy,

        breakdown,
      );
    } catch (error) {
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  // Orchestrates the async work that depends on the /hotels endpoint.
  // Performed (without await) in model() so the route transition is not blocked —
  // the page renders immediately with skeletons, and sections fill in as tasks complete.
  //
  // Sequence: fetch available comparison sets → correct invalid QPs → fire data tasks.
  // Data/pace tasks can only start after we know which comparison sets are valid.
  modelTask = task(async (params: BenchmarkInsightParams) => {
    // Only fetch hotels data if we don't already have it. The available
    // comparison sets don't change between QP updates, so we skip the fetch
    // on subsequent model() calls (e.g. when the user selects a different
    // comparison set in the dropdown).
    if (!this.beniHotelsService.fetchTask.lastSuccessful) {
      await this.beniHotelsService.fetchTask.perform();
    }

    // The route may have been torn down (e.g. navigating away or test teardown)
    // while the fetch was in flight. Bail out to avoid service lookups on a
    // destroyed owner.
    if (this.isDestroyed || this.isDestroying) {
      return;
    }

    // Mutates params in-place to replace any invalid comparison set QPs with
    // valid defaults. We intentionally do NOT trigger a route transition here —
    // the controller's fallback getters (primaryDataSetWithFallback /
    // secondaryDataSetWithFallback) handle the UI, and a transitionTo would
    // re-enter model() causing double data fetches and kicking users off
    // child routes like desired-compset.
    this.validateComparisonSetQueryParams(params);

    void this.dataApiResponseTask.perform(params);

    if (this.access.hasBenchmarkInsightPace) {
      void this.paceApiResponseTask.perform(params);
    }
  });
  paceApiResponseTask = task(async (params) => {
    const apiId = BENI_SEGMENTATIONS_API_IDS.PACE;
    const apiPath = BENI_SEGMENTATIONS_API_URLS[apiId];
    const apiModelName = BENI_SEGMENTATIONS_API_MODEL_NAMES[apiId];

    if (!apiPath || !apiModelName) {
      return null;
    }

    try {
      const requestUrl = formatUrl(
        apiPath,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.getQueryParams(params),
        this.ajax.host,
      );

      const apiResponse = await this.ajax.request(requestUrl);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload({
        [apiModelName]: apiResponse,
      });

      const peekedBeniApiResponse = this.store.peekRecord<BeniPaceApiResponse>(
        // @ts-expect-error: Incorrect type
        apiModelName,
        BENI_SEGMENTATIONS_API_IDS.PACE,
      );

      return peekedBeniApiResponse;
    } catch (error) {
      this.errorTracking.captureException(error);
    }

    return null;
  });

  // Adds a BeniBreakdownPeriodSegmentDifference record to every segmentation that is not `own`.
  // The record contains the difference in values, between the own hotel and the comparison set.
  // e.g. Difference in ADR = Comparison set ADR - Own hotel ADR

  addDifferenceToSegmentations(
    filteredSegmentations: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
  ): void {
    Object.values(BENI_COMPARISON_SETS)
      .filter(
        (comparisonSetType) => comparisonSetType !== BENI_COMPARISON_SETS.OWN,
      )
      .forEach((comparisonSetType) => {
        const segmentations = filteredSegmentations[comparisonSetType];

        if (!segmentations) {
          return;
        }

        segmentations.forEach((comparisonSetSegmentation) => {
          const matchingOwnSegmentation = filteredSegmentations[
            BENI_COMPARISON_SETS.OWN
          ]?.find(
            (ownSegmentation) =>
              ownSegmentation.label === comparisonSetSegmentation.label,
          );

          if (matchingOwnSegmentation) {
            // Create BeniBreakdownPeriodSegmentDifference record for the segmentation.
            const values: Record<Kpi, number | null> = {
              occupancy: null,
              occupancyMix: null,
              adr: null,
              revpar: null,
            };

            Object.values(KPIS).forEach((kpi) => {
              const ownValue = matchingOwnSegmentation.current.total[kpi];
              const comparisonSetValue =
                comparisonSetSegmentation.current.total[kpi];

              if (ownValue === null || comparisonSetValue === null) {
                values[kpi] = null;
              } else {
                const result = ownValue - comparisonSetValue;
                values[kpi] = result;
              }
            });

            comparisonSetSegmentation.current.total.difference =
              this.store.createRecord<BeniBreakdownPeriodSegmentDifference>(
                'benchmark-insight/breakdown-period-segment-difference',
                values,
              );
          }
        });
      });
  }

  // Some query parameters are only available to staff users, for debugging purposes.
  addQueryParamsStaffUserUtilities(
    params: BenchmarkInsightParams,
    queryParams: FormatUrlParams,
  ): void {
    if (!this.access.isStaffUser) {
      return;
    }

    const { breakdownQuery, extractDate } = params;

    if (breakdownQuery) {
      queryParams['breakdown_query'] = breakdownQuery;
    }

    if (extractDate) {
      queryParams['debug_extract_date'] = extractDate;
    }
  }

  beforeModel(transition: Transition): void {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const validateQueryParams = () => {
      if (!this.hasSetQueryParametersFromLocalStorage) {
        this.setLocalStorageQueryParams(transition);
      }

      this.transition.validateBenchmarkInsightQueryParams(transition);
    };

    if (!this.access.hasBenchmarkInsight) {
      if (
        this.access.hasBenchmarkInsightLightUpsell ||
        this.access.hasBenchmarkInsightTrialUpsell
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
        this.router.legacyTransitionTo('hotel.benchmark-insight.upsell');
      } else {
        this.transition.redirectToEnabledTab(transition);
      }
    } else {
      if (!this.access.hasBenchmarkInsightData) {
        const { queryParams } = transition.to;

        const viewAsStaffUser = Boolean(queryParams['viewAsStaffUser']);
        if (this.isViewingAsStaffUser(viewAsStaffUser)) {
          validateQueryParams();

          this.transitionToOnboardingRoute();
          return;
        }

        const { benchmarkInsightTermsAndConditionsAccepted } =
          this.hotelAndMarketService.hotel;

        if (!benchmarkInsightTermsAndConditionsAccepted) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line  @typescript-eslint/no-unsafe-call
          this.router.legacyTransitionTo(
            'hotel.benchmark-insight.terms-and-conditions',
          );
          return;
        }
      }

      validateQueryParams();

      this.transitionToOnboardingRoute();
    }
  }

  getQueryParams(params: BenchmarkInsightParams): FormatUrlParams {
    const { segment, pickup, los, fromDate, toDate, referencePeriod, channel } =
      params;
    const { hasBenchmarkInsightFutureData } = this.access;
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;
    const { id } = this.hotelAndMarketService.hotel;

    // Fetch next 30 days of data by default.
    let fallbackStayDateStart = this.hotelAndMarketService
      .toLocalTimeWithFallback(fromDate)
      .format('YYYY-MM-DD');

    let fallbackStayDateEnd = this.hotelAndMarketService
      .getLocalTime()
      .add(DEFAULT_DAYS_SHOWN_IN_BENI, 'day')
      .format('YYYY-MM-DD');

    // If future data is not allowed, fetch last 30 days of data by default instead.
    if (!hasBenchmarkInsightFutureData) {
      const { lastPastDate } = this.benchmarkInsightBeniService;

      fallbackStayDateStart = lastPastDate
        .subtract(DEFAULT_DAYS_SHOWN_IN_BENI, 'day')
        .format('YYYY-MM-DD');

      fallbackStayDateEnd = lastPastDate.format('YYYY-MM-DD');
    }

    const stayDateStart = fromDate ?? fallbackStayDateStart;
    const stayDateEnd = toDate ?? fallbackStayDateEnd;

    const primaryComparisonSetWithFallback =
      params.primaryDataSet ?? allowedComparisonSets[0] ?? null;
    let secondaryComparisonSetWithFallback =
      params.secondaryDataSet ?? allowedComparisonSets[1] ?? null;

    // Backend expects `null` instead of `none`.
    if (
      secondaryComparisonSetWithFallback ===
      DROPDOWN_COMPARISON_SETS_OPTION_NONE
    ) {
      secondaryComparisonSetWithFallback = 'null';
    }

    const queryParams: FormatUrlParams = {
      stay_date_start: stayDateStart,
      stay_date_end: stayDateEnd,
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
      subscription_id: id,
      // QP which determines whether channel data is included in the response.
      // Can only be removed after backend is updated to not require this QP.
      breakdown_query: 3,
    };

    if (primaryComparisonSetWithFallback) {
      queryParams['primary_comparison_set'] = primaryComparisonSetWithFallback;
    }

    if (secondaryComparisonSetWithFallback) {
      queryParams['secondary_comparison_set'] =
        secondaryComparisonSetWithFallback;
    }

    if (referencePeriod) {
      queryParams['ref_period'] = referencePeriod;
    }

    if (pickup) {
      queryParams['pickup'] = pickup;
    }

    if (los) {
      queryParams['los[]'] = los;
    }

    if (segment && segment !== ALL_SEGMENTS_OPTION.id) {
      queryParams['segment'] = segment;
    }

    if (channel && this.access.hasBenchmarkInsightChannel) {
      queryParams['channel'] = channel;
    }

    queryParams['currency'] = this.beniService.beniCurrency;

    this.addQueryParamsStaffUserUtilities(params, queryParams);

    return queryParams;
  }

  isHierarchyResponse(object: unknown): object is HierarchyResponse {
    return (
      typeof object === 'object' &&
      object !== null &&
      'hierarchy' in object &&
      Array.isArray(object.hierarchy)
    );
  }

  isViewingAsStaffUser(viewAsStaffUserParam: boolean): boolean {
    return viewAsStaffUserParam && this.access.hasBenchmarkInsightDataStaffOnly;
  }

  // Fetches BENI hotels data, validates comparison set QPs, then fires
  // comparison-set-dependent data tasks. Performed without awaiting in model()

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: BenchmarkInsightParams) {
    const viewAsStaffUser = Boolean(params['viewAsStaffUser']);

    // Terms and conditions & upsell
    // Don't fetch data if we redirect to a child route, we don't want data loading in the background of overlays.
    if (
      !this.access.hasBenchmarkInsightData &&
      !this.isViewingAsStaffUser(viewAsStaffUser)
    ) {
      return {};
    }

    const model: ModelType = {};

    // --- Independent tasks: don't need comparison set data, fire immediately ---
    model.hierarchy = this.fetchHierarchyTask.perform(
      PMS_MAPPING_TABS.MARKET_CODE,
    );

    if (this.access.hasBenchmarkInsightChannel) {
      model.hierarchyChannel = this.fetchHierarchyTask.perform(
        BREAKDOWNS.CHANNEL,
      );
    }

    if (!this.access.hasBenchmarkInsightCompset) {
      model.desiredCompset =
        this.benchmarkInsightCompetitorsDesiredCompset.desiredCompsetTask;
      void model.desiredCompset.perform();
    }

    if (this.access.hasBenchmarkInsightTrialUpsell) {
      model.upsell = this.benchmarkInsightTrial.upsellApiResponseTask;
      void model.upsell.perform();
    }

    // --- Comparison-set-dependent tasks: managed by modelTask ---
    // We expose the task objects (not their results) on the model so the template
    // can reactively bind to .isRunning and .last.value for loading/data states.
    // The tasks themselves are performed inside modelTask after the /hotels fetch.
    // @ts-expect-error: Type 'TaskForAsyncTaskFunction<unknown, (params: any) => Promise<BeniDataApiResponse | null>>' is not assignable to type 'Task<string, [params: BenchmarkInsightParams]>'.
    model.dataApiResponse = this.dataApiResponseTask;

    if (this.access.hasBenchmarkInsightPace) {
      // @ts-expect-error: Type 'TaskForAsyncTaskFunction<unknown, (params: any) => Promise<BeniPaceApiResponse | null>>' is not assignable to type 'Task<string, [params: BenchmarkInsightParams]>'.
      model.paceApiResponse = this.paceApiResponseTask;
    }

    // Kick off the async orchestration task without awaiting — this lets model()
    // return immediately so the route transition is instant. The template shows
    // skeletons while modelTask.isRunning is true.
    model.modelTask = this.modelTask;
    void this.modelTask.perform(params);

    return model;
  }

  setLocalStorageQueryParams(transition: Transition): void {
    const LOCAL_STORAGE_TO_QP_MAPPING = {
      [BENI_LOCAL_STORAGE_KEYS.SELECTED_STAY_DATE_SECTION_VIEW]:
        'stayDateSectionView',
      [BENI_LOCAL_STORAGE_KEYS.SELECTED_KPI_HERO_CHART]: 'selectedKpiHeroChart',
      [BENI_LOCAL_STORAGE_KEYS.SELECTED_KPI_LOS_CHART]: 'selectedKpiLosChart',
      [BENI_LOCAL_STORAGE_KEYS.SELECTED_LOS_SORT_OPTION]:
        'selectedLosSortOption',
      [BENI_LOCAL_STORAGE_KEYS.SELECTED_AXIS_OPTION]: 'selectedAxisOption',
      [BENI_LOCAL_STORAGE_KEYS.REFERENCE_PERIOD]: 'referencePeriod',
      [BENI_LOCAL_STORAGE_KEYS.PICKUP]: 'pickup',
      [BENI_LOCAL_STORAGE_KEYS.PRIMARY_DATA_SET]: 'primaryDataSet',
      [BENI_LOCAL_STORAGE_KEYS.SECONDARY_DATA_SET]: 'secondaryDataSet',
      [BENI_LOCAL_STORAGE_KEYS.EXPANDED_BREAKDOWNS]: 'expandedBreakdowns',
    };

    for (const [localStorageKey, queryParamKey] of Object.entries(
      LOCAL_STORAGE_TO_QP_MAPPING,
    )) {
      const value = this.localStorage.getItem(localStorageKey);
      if (value) {
        transition.to.queryParams[queryParamKey] = value;
      }
    }

    this.hasSetQueryParametersFromLocalStorage = true;
  }

  transitionToOnboardingRoute(): void {
    if (
      this.hotelAndMarketService.hotel.benchmarkInsightIsLight &&
      !this.userEngagement.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.BENCHMARK_INSIGHT_LIGHT_ONBOARDING_MODAL,
      )
    ) {
      this.router.transitionTo('hotel.benchmark-insight.onboarding');
    }
  }

  // Corrects primaryDataSet/secondaryDataSet in params based on allowedComparisonSets.
  // Mutates params in-place so the data tasks use valid comparison sets.
  // No route transition is needed because the controller's fallback getters
  // (primaryDataSetWithFallback / secondaryDataSetWithFallback) handle the UI.
  validateComparisonSetQueryParams(params: BenchmarkInsightParams): void {
    this.transition.checkBenchmarkInsightDataSets(
      params as Parameters<
        typeof this.transition.checkBenchmarkInsightDataSets
      >[0],
    );
  }
}
